export interface EMIResult {
  monthlyEMI: number
  totalInterest: number
  totalPayment: number
  processingFeeWithGST: number
}

export interface AmortizationEntry {
  month: number
  emiPaid: number
  principalPaid: number
  interestPaid: number
  remainingBalance: number
}

export interface PartialPaymentResult {
  originalTenure: number
  newTenure: number
  interestSaved: number
  originalTotalInterest: number
  newTotalInterest: number
}

export function calculateEMI(
  principal: number,
  annualRate: number,
  tenureMonths: number
): EMIResult {
  if (principal <= 0 || annualRate <= 0 || tenureMonths <= 0) {
    return { monthlyEMI: 0, totalInterest: 0, totalPayment: 0, processingFeeWithGST: 0 }
  }

  const monthlyRate = annualRate / 12 / 100
  const emi =
    (principal * monthlyRate * Math.pow(1 + monthlyRate, tenureMonths)) /
    (Math.pow(1 + monthlyRate, tenureMonths) - 1)

  const totalPayment = emi * tenureMonths
  const totalInterest = totalPayment - principal

  // Processing fee: typically 1-2% of loan amount
  const processingFee = principal * 0.01
  const gstOnProcessingFee = processingFee * 0.18
  const processingFeeWithGST = processingFee + gstOnProcessingFee

  return {
    monthlyEMI: Math.round(emi),
    totalInterest: Math.round(totalInterest),
    totalPayment: Math.round(totalPayment),
    processingFeeWithGST: Math.round(processingFeeWithGST),
  }
}

export function generateAmortizationSchedule(
  principal: number,
  annualRate: number,
  tenureMonths: number
): AmortizationEntry[] {
  if (principal <= 0 || annualRate <= 0 || tenureMonths <= 0) return []

  const monthlyRate = annualRate / 12 / 100
  const emi =
    (principal * monthlyRate * Math.pow(1 + monthlyRate, tenureMonths)) /
    (Math.pow(1 + monthlyRate, tenureMonths) - 1)

  const schedule: AmortizationEntry[] = []
  let balance = principal

  for (let month = 1; month <= tenureMonths; month++) {
    const interestPaid = balance * monthlyRate
    const principalPaid = emi - interestPaid
    balance = balance - principalPaid

    schedule.push({
      month,
      emiPaid: Math.round(emi),
      principalPaid: Math.round(principalPaid),
      interestPaid: Math.round(interestPaid),
      remainingBalance: Math.max(0, Math.round(balance)),
    })
  }

  return schedule
}

export function calculatePartialPayment(
  principal: number,
  annualRate: number,
  tenureMonths: number,
  partialPaymentAmount: number,
  partialPaymentMonth: number
): PartialPaymentResult {
  if (principal <= 0 || annualRate <= 0 || tenureMonths <= 0 || partialPaymentAmount <= 0) {
    return {
      originalTenure: tenureMonths,
      newTenure: tenureMonths,
      interestSaved: 0,
      originalTotalInterest: 0,
      newTotalInterest: 0,
    }
  }

  const monthlyRate = annualRate / 12 / 100
  const emi =
    (principal * monthlyRate * Math.pow(1 + monthlyRate, tenureMonths)) /
    (Math.pow(1 + monthlyRate, tenureMonths) - 1)

  // Original total interest
  const originalTotalInterest = emi * tenureMonths - principal

  // Calculate balance at partial payment month
  let balance = principal
  let totalInterestPaidBeforePartial = 0

  for (let m = 1; m <= Math.min(partialPaymentMonth, tenureMonths); m++) {
    const interest = balance * monthlyRate
    const principalPaid = emi - interest
    totalInterestPaidBeforePartial += interest
    balance -= principalPaid
  }

  // Apply partial payment
  balance = Math.max(0, balance - partialPaymentAmount)

  if (balance <= 0) {
    return {
      originalTenure: tenureMonths,
      newTenure: partialPaymentMonth,
      interestSaved: Math.round(originalTotalInterest - totalInterestPaidBeforePartial),
      originalTotalInterest: Math.round(originalTotalInterest),
      newTotalInterest: Math.round(totalInterestPaidBeforePartial),
    }
  }

  // Calculate new tenure with same EMI
  let newMonths = 0
  let totalInterestAfterPartial = 0

  while (balance > 0 && newMonths < 600) {
    const interest = balance * monthlyRate
    const principalPaid = emi - interest
    if (principalPaid <= 0) break
    totalInterestAfterPartial += interest
    balance -= principalPaid
    newMonths++
  }

  const newTotalInterest = totalInterestPaidBeforePartial + totalInterestAfterPartial
  const newTenure = partialPaymentMonth + newMonths

  return {
    originalTenure: tenureMonths,
    newTenure,
    interestSaved: Math.round(originalTotalInterest - newTotalInterest),
    originalTotalInterest: Math.round(originalTotalInterest),
    newTotalInterest: Math.round(newTotalInterest),
  }
}

export function formatCurrency(amount: number): string {
  if (amount >= 10000000) {
    return `${(amount / 10000000).toFixed(2)} Cr`
  }
  if (amount >= 100000) {
    return `${(amount / 100000).toFixed(2)} L`
  }
  return amount.toLocaleString("en-IN")
}

export function formatCurrencyFull(amount: number): string {
  return `\u20B9${amount.toLocaleString("en-IN")}`
}
